import { useEffect, useMemo, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Facebook, Sparkles, Send, Save, Wand2, ImagePlus, ExternalLink, RefreshCw, ThumbsUp, MessageCircle, Share2, MoreHorizontal, Globe, Eye, Upload, Trash2, Loader2, Pencil, X, Play, Download, Copy, Check, UserCircle, Users, CheckCircle2, Shield, LayoutTemplate, Info, Crop, CalendarDays } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format, parse } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { copyTextReliable } from "@/lib/clipboard";
import { FB_TEMPLATES, orderTemplates } from "@/lib/fbTemplates";
import FbShareTuto from "@/components/FbShareTuto";

const MAX_MEDIA = 4;

const EMPTY = {
  title: "", text: "", post_type: "actualite", offer_text: "",
  desired_date: "", media: [],
  phone: "", address: "", website: "", destination_url: "", extra: "",
};

/** Upload de médias (jusqu'à 4 photos/vidéos), drag & drop + click, avec prévisualisation. */
function cropImageToRatio(url, ratio, pos) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const iw = img.naturalWidth, ih = img.naturalHeight;
      let cw = iw, ch = iw / ratio;
      if (ch > ih) { ch = ih; cw = ih * ratio; }
      const sx = Math.max(0, (iw - cw) / 2);
      const sy = pos === "top" ? 0 : pos === "bottom" ? (ih - ch) : (ih - ch) / 2;
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(cw); canvas.height = Math.round(ch);
      canvas.getContext("2d").drawImage(img, sx, sy, cw, ch, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("blob"))), "image/jpeg", 0.9);
    };
    img.onerror = reject;
    img.src = url;
  });
}

/** Recadre une image à partir d'un rectangle exprimé en pixels NATURELS de l'image source. */
function cropImageToRect(url, rect) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const sx = Math.max(0, Math.round(rect.x));
      const sy = Math.max(0, Math.round(rect.y));
      const sw = Math.max(1, Math.round(rect.w));
      const sh = Math.max(1, Math.round(rect.h));
      const canvas = document.createElement("canvas");
      canvas.width = sw; canvas.height = sh;
      canvas.getContext("2d").drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("blob"))), "image/jpeg", 0.9);
    };
    img.onerror = reject;
    img.src = url;
  });
}

/** Sélecteur de zone de recadrage LIBRE : l'utilisateur dessine un rectangle à la souris/au doigt.
 *  Le rectangle sélectionné (en pixels naturels) est remonté via rectRef pour validation manuelle. */
function FreeCropSelector({ url, rectRef }) {
  const imgRef = useRef(null);
  const [sel, setSel] = useState(null); // en pixels d'affichage
  const drag = useRef(null);

  const clampPoint = (clientX, clientY) => {
    const b = imgRef.current.getBoundingClientRect();
    return {
      x: Math.min(Math.max(clientX - b.left, 0), b.width),
      y: Math.min(Math.max(clientY - b.top, 0), b.height),
    };
  };
  const pushRect = (s) => {
    const img = imgRef.current;
    if (!img || !s || s.w < 8 || s.h < 8) { rectRef.current = null; return; }
    const scale = img.naturalWidth / img.clientWidth;
    rectRef.current = { x: s.x * scale, y: s.y * scale, w: s.w * scale, h: s.h * scale };
  };
  const onDown = (e) => {
    e.preventDefault();
    const p = clampPoint(e.clientX, e.clientY);
    drag.current = p;
    setSel({ x: p.x, y: p.y, w: 0, h: 0 });
  };
  const onMove = (e) => {
    if (!drag.current) return;
    const p = clampPoint(e.clientX, e.clientY);
    const s = {
      x: Math.min(drag.current.x, p.x), y: Math.min(drag.current.y, p.y),
      w: Math.abs(p.x - drag.current.x), h: Math.abs(p.y - drag.current.y),
    };
    setSel(s); pushRect(s);
  };
  const onUp = () => { drag.current = null; };

  return (
    <div className="select-none">
      <div
        className="relative mx-auto w-fit touch-none cursor-crosshair"
        onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp}
        data-testid="pro-fb-freecrop-canvas">
        <img ref={imgRef} src={url} alt="" draggable={false}
             onLoad={() => { rectRef.current = null; setSel(null); }}
             style={{ maxHeight: "44vh", maxWidth: "100%", display: "block" }} />
        {sel && sel.w > 2 && sel.h > 2 && (
          <div className="absolute border-2 border-[#E05638] pointer-events-none"
               style={{ left: sel.x, top: sel.y, width: sel.w, height: sel.h, boxShadow: "0 0 0 9999px rgba(0,0,0,0.45)" }} />
        )}
      </div>
      <p className="mt-2 text-[11px] text-slate-500 text-center">Dessinez un rectangle sur l'image pour choisir la zone exacte.</p>
    </div>
  );
}

function MediaUploader({ media, onChange, disabled, onRegenerate, regeneratingIdx }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [cropIdx, setCropIdx] = useState(-1);
  const [cropMode, setCropMode] = useState("format"); // "format" | "free"
  const [cropRatio, setCropRatio] = useState(1);
  const [cropPos, setCropPos] = useState("center");
  const [cropping, setCropping] = useState(false);
  const freeRectRef = useRef(null);
  const items = media || [];
  const remaining = MAX_MEDIA - items.length;

  const uploadOne = async (file, { preserveCrop = false } = {}) => {
    const fd = new FormData();
    fd.append("file", file);
    if (preserveCrop) fd.append("preserve_crop", "1");
    const { data } = await api.post("/pro/fb-posts/upload-media", fd, {
      onUploadProgress: (evt) => { if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100)); },
    });
    return { type: data.type, url: data.url };
  };

  const handleFiles = async (files) => {
    if (!files || files.length === 0 || disabled) return;
    const list = Array.from(files).slice(0, remaining);
    if (list.length === 0) { toast.warning(`Maximum ${MAX_MEDIA} médias.`); return; }
    setUploading(true); setProgress(0);
    const uploaded = [];
    for (const f of list) {
      const isImg = /^image\//.test(f.type);
      const isVid = /^video\//.test(f.type);
      if (!isImg && !isVid) { toast.error(`${f.name} : format non supporté.`); continue; }
      if (isImg && f.size > 6 * 1024 * 1024) { toast.error(`${f.name} > 6 Mo.`); continue; }
      if (isVid && f.size > 60 * 1024 * 1024) { toast.error(`${f.name} > 60 Mo.`); continue; }
      try { uploaded.push(await uploadOne(f)); }
      catch (e) { toast.error(`Échec upload ${f.name} : ${e?.response?.data?.detail || e?.message || "erreur"}`); }
    }
    if (uploaded.length) { onChange([...items, ...uploaded].slice(0, MAX_MEDIA)); toast.success(`${uploaded.length} média(s) ajouté(s).`); }
    setUploading(false); setProgress(0);
  };

  const removeAt = (i) => onChange(items.filter((_, k) => k !== i));
  const replaceAt = (i, patch) => onChange(items.map((m, k) => (k === i ? { ...m, ...patch } : m)));
  const applyCrop = async () => {
    const m = items[cropIdx];
    if (!m) return;
    setCropping(true);
    try {
      let blob;
      if (cropMode === "free") {
        const r = freeRectRef.current;
        if (!r) { toast.warning("Sélectionnez d'abord une zone sur l'image."); setCropping(false); return; }
        blob = await cropImageToRect(m.url, r);
      } else {
        blob = await cropImageToRatio(m.url, cropRatio, cropPos);
      }
      const up = await uploadOne(new File([blob], "crop.jpg", { type: "image/jpeg" }), { preserveCrop: true });
      replaceAt(cropIdx, { url: up.url });
      toast.success("Image recadrée.");
      setCropIdx(-1);
    } catch (e) {
      toast.error("Le recadrage a échoué.");
    } finally { setCropping(false); }
  };
  const openCrop = (i) => { setCropIdx(i); setCropMode("format"); setCropRatio(1); setCropPos("center"); freeRectRef.current = null; };

  return (
    <div>
      <Label className="flex items-center gap-1"><ImagePlus size={14}/> Photos &amp; vidéos (jusqu'à {MAX_MEDIA} — images 6 Mo, vidéos MP4/MOV 60 Mo)</Label>
      <div className="mt-1 grid grid-cols-2 sm:grid-cols-4 gap-2">
        {items.map((m, i) => (
          <div key={m.url || i} className="relative group aspect-video rounded-xl overflow-hidden border border-slate-200 bg-slate-900" data-testid={`pro-fb-media-thumb-${i}`}>
            {m.type === "video"
              ? <video src={m.url} className="w-full h-full object-cover" muted/>
              : <img src={m.url} alt="" className="w-full h-full object-cover"/>}
            <button type="button" onClick={()=>removeAt(i)}
                    className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 border border-slate-200 grid place-items-center text-rose-600 hover:bg-white shadow"
                    data-testid={`pro-fb-media-remove-${i}`} aria-label="Retirer"><Trash2 size={14}/></button>
            <div className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded bg-black/60 text-white text-[9px] font-bold uppercase">{m.type === "video" ? "Vidéo" : "Image"}</div>
            {m.type !== "video" && (
              <div className="absolute bottom-1 right-1 flex gap-1">
                {m.ai && onRegenerate && (
                  <button type="button" onClick={()=>onRegenerate(i)} disabled={regeneratingIdx===i}
                          className="w-7 h-7 rounded-full bg-white/90 border border-slate-200 grid place-items-center text-purple-600 hover:bg-white shadow"
                          data-testid={`pro-fb-media-regen-${i}`} aria-label="Régénérer l'image IA">
                    <RefreshCw size={12} className={regeneratingIdx===i ? "animate-spin" : ""}/>
                  </button>
                )}
                <button type="button" onClick={()=>openCrop(i)}
                        className="w-7 h-7 rounded-full bg-white/90 border border-slate-200 grid place-items-center text-slate-700 hover:bg-white shadow"
                        data-testid={`pro-fb-media-crop-${i}`} aria-label="Recadrer"><Crop size={12}/></button>
              </div>
            )}
          </div>
        ))}
        {remaining > 0 && (
          <label className={`aspect-video rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-1 cursor-pointer transition-colors ${dragOver ? "border-[#E05638] bg-orange-50" : "border-slate-300 hover:border-[#E05638] hover:bg-orange-50/50"} ${disabled ? "opacity-50 pointer-events-none" : ""}`}
               onDragOver={(e)=>{ e.preventDefault(); setDragOver(true); }} onDragLeave={()=>setDragOver(false)}
               onDrop={(e)=>{ e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
               data-testid="pro-fb-media-dropzone">
            <input type="file" accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/webm" multiple className="hidden"
                   onChange={(e)=>{ handleFiles(e.target.files); e.target.value = ""; }} disabled={uploading} data-testid="pro-fb-media-input"/>
            {uploading ? (
              <><Loader2 size={20} className="text-[#E05638] animate-spin"/><div className="text-[11px] font-semibold text-slate-700">Envoi… {progress}%</div></>
            ) : (
              <><Upload size={20} className="text-slate-500"/><div className="text-[11px] font-semibold text-slate-700">Déposez ou cliquez</div><div className="text-[10px] text-slate-500">{remaining} restant(s)</div></>
            )}
          </label>
        )}
      </div>
      <p className="mt-1.5 text-[11px] text-slate-500 flex items-center gap-1">
        <Shield size={12} className="text-[#E05638]"/> Une petite pastille PROXIreduc est ajoutée en haut à droite de vos images.
      </p>
      {cropIdx >= 0 && items[cropIdx] && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" data-testid="pro-fb-crop-modal">
          <div className="bg-white rounded-2xl p-4 max-w-md w-full">
            <div className="font-heading font-bold text-slate-900 mb-3 flex items-center gap-2"><Crop size={16} className="text-[#E05638]"/> Recadrer l'image</div>
            <div className="mb-3 inline-flex rounded-full border border-slate-200 p-0.5 bg-slate-50" data-testid="pro-fb-crop-mode">
              {[["Formats", "format"], ["Zone libre", "free"]].map(([l, mv]) => (
                <button key={mv} type="button" onClick={()=>setCropMode(mv)}
                        className={`px-3.5 py-1 rounded-full text-sm font-semibold transition-colors ${cropMode === mv ? "bg-white text-[#E05638] shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                        data-testid={`pro-fb-crop-mode-${mv}`}>{l}</button>
              ))}
            </div>
            {cropMode === "free" ? (
              <FreeCropSelector url={items[cropIdx].url} rectRef={freeRectRef} />
            ) : (
              <>
                <div className="rounded-xl overflow-hidden border border-slate-200 bg-slate-900 mx-auto" style={{ aspectRatio: String(cropRatio), maxHeight: "40vh" }}>
                  <img src={items[cropIdx].url} alt="" className="w-full h-full object-cover"
                       style={{ objectPosition: cropPos === "top" ? "top" : cropPos === "bottom" ? "bottom" : "center" }}/>
                </div>
                <div className="mt-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Format</div>
                <div className="mt-1 flex gap-2 flex-wrap">
                  {[["Carré", 1], ["Portrait", 4 / 5], ["Paysage", 1.91]].map(([l, r]) => (
                    <button key={l} type="button" onClick={()=>setCropRatio(r)}
                            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${Math.abs(cropRatio - r) < 0.01 ? "bg-[#E05638] text-white border-[#E05638]" : "border-slate-200 hover:bg-slate-50"}`}
                            data-testid={`pro-fb-crop-ratio-${l}`}>{l}</button>
                  ))}
                </div>
                <div className="mt-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Cadrage</div>
                <div className="mt-1 flex gap-2">
                  {[["Haut", "top"], ["Centre", "center"], ["Bas", "bottom"]].map(([l, p]) => (
                    <button key={p} type="button" onClick={()=>setCropPos(p)}
                            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${cropPos === p ? "bg-slate-800 text-white border-slate-800" : "border-slate-200 hover:bg-slate-50"}`}
                            data-testid={`pro-fb-crop-pos-${p}`}>{l}</button>
                  ))}
                </div>
              </>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={()=>setCropIdx(-1)} disabled={cropping} data-testid="pro-fb-crop-cancel">Annuler</Button>
              <Button type="button" onClick={applyCrop} disabled={cropping} className="btn-shine text-white" data-testid="pro-fb-crop-apply">{cropping ? "Recadrage…" : "Appliquer"}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Prévisualisation fidèle d'un post Facebook (jusqu'à 4 images en grille) */
function FbPreview({ title, text, images, videos, destinationUrl, businessName, businessCity }) {
  const initials = (businessName || "P").split(/\s+/).slice(0, 2).map((w)=>w[0]?.toUpperCase()).join("");
  const fullText = [title?.trim(), text?.trim()].filter(Boolean).join("\n\n");
  const [expanded, setExpanded] = useState(false);
  const isLong = (fullText || "").length > 260;
  const shown = expanded || !isLong ? fullText : (fullText || "").slice(0, 260).replace(/\s+\S*$/, "") + "…";

  const renderRich = (t) => (t || "").split(/(\s+)/).map((chunk, i) => {
    if (/^#\w+/.test(chunk)) return <span key={i} className="text-blue-600 font-medium">{chunk}</span>;
    if (/^https?:\/\//.test(chunk)) return <a key={i} href={chunk} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">{chunk}</a>;
    return <span key={i}>{chunk}</span>;
  });

  const imgs = (images || []).filter(Boolean);
  const gridCls = imgs.length === 1 ? "grid-cols-1" : imgs.length === 3 ? "grid-cols-2" : "grid-cols-2";

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden font-[system-ui,-apple-system,Segoe_UI,Roboto,sans-serif]" data-testid="pro-fb-preview">
      <div className="p-3 flex items-start gap-2">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#E05638] to-orange-500 text-white grid place-items-center font-bold text-sm shrink-0">{initials || "P"}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 flex-wrap">
            <span className="font-semibold text-[14px] text-slate-900 hover:underline cursor-pointer truncate">{businessName || "Votre page"}</span>
            {businessCity && <span className="text-[12px] text-slate-500">· {businessCity}</span>}
          </div>
          <div className="flex items-center gap-1 text-[12px] text-slate-500">
            <span>À l'instant</span><span>·</span><Globe size={11}/>
          </div>
        </div>
        <MoreHorizontal size={18} className="text-slate-500 shrink-0"/>
      </div>

      {(fullText || "").trim() ? (
        <div className="px-3 pb-2 text-[14px] text-slate-900 leading-[1.35] whitespace-pre-line break-words" data-testid="pro-fb-preview-text">
          {renderRich(shown)}
          {isLong && !expanded && (
            <button onClick={()=>setExpanded(true)} className="ml-1 text-slate-500 hover:underline font-medium">Voir plus</button>
          )}
        </div>
      ) : (
        <div className="px-3 pb-2 text-[13px] text-slate-400 italic">Votre texte apparaîtra ici…</div>
      )}

      {imgs.length > 0 && (
        <div className={`grid ${gridCls} gap-0.5 bg-slate-100`}>
          {imgs.map((src, i) => (
            <div key={src || i} className={`relative ${imgs.length === 1 ? "aspect-[1.91/1]" : "aspect-square"} ${imgs.length === 3 && i === 0 ? "col-span-2" : ""} bg-slate-200`}>
              <img src={src} alt="" className="absolute inset-0 w-full h-full object-cover" onError={(e)=>{ e.currentTarget.style.display="none"; }}/>
            </div>
          ))}
        </div>
      )}

      {(videos || []).length > 0 && (
        <div className="bg-black" data-testid="pro-fb-preview-video">
          <video src={videos[0]} controls className="w-full max-h-72"/>
        </div>
      )}
      {destinationUrl && !imgs.length && (
        <a href={destinationUrl} target="_blank" rel="noreferrer" className="block border-t border-slate-200 bg-slate-50 hover:bg-slate-100 transition-colors">
          <div className="p-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{(()=>{ try { return new URL(destinationUrl).hostname.replace(/^www\./, ""); } catch { return "lien"; } })()}</div>
            <div className="text-[14px] text-slate-900 font-semibold truncate">En savoir plus</div>
          </div>
        </a>
      )}

      <div className="px-3 py-2 flex items-center justify-between text-[12px] text-slate-500 border-t border-slate-100">
        <div className="flex items-center gap-1">
          <span className="w-4 h-4 rounded-full bg-blue-500 text-white grid place-items-center text-[9px]">👍</span>
          <span className="w-4 h-4 -ml-1 rounded-full bg-rose-500 text-white grid place-items-center text-[9px]">❤</span>
          <span className="ml-1 italic text-slate-400">Aperçu avant publication</span>
        </div>
      </div>

      <div className="grid grid-cols-3 border-t border-slate-100 text-[13px] font-semibold text-slate-600">
        <button className="flex items-center justify-center gap-1.5 py-2 hover:bg-slate-50"><ThumbsUp size={16}/> J'aime</button>
        <button className="flex items-center justify-center gap-1.5 py-2 hover:bg-slate-50"><MessageCircle size={16}/> Commenter</button>
        <button className="flex items-center justify-center gap-1.5 py-2 hover:bg-slate-50"><Share2 size={16}/> Partager</button>
      </div>
    </div>
  );
}

export default function ProFbPosts() {
  const [posts, setPosts] = useState([]);
  const [entity, setEntity] = useState({ name: "Votre page", city: "", label: "votre page" });
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [justPublished, setJustPublished] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);
  const [aiBrief, setAiBrief] = useState("");
  const [aiTone, setAiTone] = useState("chaleureux");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiIncludeContact, setAiIncludeContact] = useState(true);
  const [aiImgLoading, setAiImgLoading] = useState(false);
  const [regenIdx, setRegenIdx] = useState(-1);
  const [rememberContact, setRememberContact] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingIds, setPendingIds] = useState([]);
  const location = useLocation();

  useEffect(() => {
    const pf = location.state?.prefill;
    if (pf) {
      setForm((s) => ({ ...s, ...pf }));
      setEditingId(null);
      window.history.replaceState({}, document.title);
      toast.success("Promo pré-remplie : complétez et publiez sur vos réseaux et groupes !");
      return;
    }
    // Pré-remplissage depuis l'e-mail "Promo validée" (?offre=<id>)
    const offerId = new URLSearchParams(location.search).get("offre");
    if (offerId) {
      api.get("/pro/offers").then((r) => {
        const o = (r.data || []).find((x) => x.id === offerId);
        if (!o) return;
        setForm((s) => ({ ...s,
          post_type: "offre",
          title: o.title || "",
          offer_text: o.discount ? `${o.title} — ${o.discount}` : (o.title || ""),
          text: `${o.title || ""}${o.discount ? ` — ${o.discount}` : ""}${o.description ? `\n${o.description}` : ""}\nProfitez-en vite, offre à partager autour de vous !`,
        }));
        setEditingId(null);
        toast.success("Promo pré-remplie : complétez et publiez sur vos réseaux et groupes !");
      }).catch(() => {});
      window.history.replaceState({}, document.title, location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([
        api.get("/pro/fb-posts"),
        api.get("/fb-posts/context").catch(() => ({ data: { entity: {} } })),
      ]);
      setPosts(p.data || []);
      setSelected(new Set());
      if (c.data?.entity) setEntity(c.data.entity);
    } catch (e) {
      toast.error("Erreur de chargement.");
    } finally { setLoading(false); }
  };
  useEffect(() => { loadAll(); }, []);

  // Pré-remplit les coordonnées mémorisées lors des précédents posts
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("pro_fb_contact") || "null");
      if (saved) setForm((s) => ({
        ...s,
        phone: s.phone || saved.phone || "",
        website: s.website || saved.website || "",
        address: s.address || saved.address || "",
        destination_url: s.destination_url || saved.destination_url || "",
        offer_text: s.offer_text || saved.offer_text || "",
      }));
    } catch (err) { console.debug(err); }
  }, []);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => (posts.length && s.size === posts.length) ? new Set() : new Set(posts.map((p) => p.id)));
  const doBulkDelete = async () => {
    const ids = pendingIds;
    if (!ids.length) return;
    setDeleting(true);
    try {
      const { data } = await api.post("/pro/fb-posts/bulk-delete", { ids });
      toast.success(`${data.deleted} publication(s) supprimée(s).`);
      setConfirmOpen(false);
      setPendingIds([]);
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Suppression impossible."); }
    finally { setDeleting(false); }
  };

  const previewUrl = (id) => `${API}/fb-posts/${id}/public`;
  const openPreview = (id) => { try { window.open(previewUrl(id), "_blank", "noopener"); } catch (err) { console.debug(err); } };

  const startEdit = (p) => {
    setEditingId(p.id);
    setJustPublished(null);
    setForm({
      title: p.title || "", text: p.text || "", post_type: p.post_type || "actualite",
      offer_text: p.offer_text || "", desired_date: p.desired_date || "",
      media: (p.media && p.media.length) ? p.media : (p.image_url ? [{ type: "image", url: p.image_url }] : []),
      phone: p.phone || "", address: p.address || "", website: p.website || "",
      destination_url: p.destination_url || "", extra: p.extra || "",
    });
    try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch (err) { console.debug("scrollTo failed", err); }
  };
  const cancelEdit = () => { setEditingId(null); setForm(EMPTY); };

  const applyTemplate = (t) => {
    setForm((s) => ({ ...s, title: t.title, text: t.text, post_type: t.post_type || s.post_type }));
    setTplOpen(false);
    toast.success("Modèle inséré — personnalisez les [champs] entre crochets.");
    try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch (err) { console.debug(err); }
  };

  const submit = async (asDraft, resetAfter = true) => {
    if (!form.title.trim() || form.title.trim().length < 2) { toast.error("Titre trop court."); return; }
    if (!form.text.trim() || form.text.trim().length < 10) { toast.error("Texte trop court (10 caractères minimum)."); return; }
    let previewWin = null;
    if (!asDraft) { try { previewWin = window.open("about:blank", "_blank"); } catch (err) { console.debug(err); } }
    setSaving(true);
    if (rememberContact) {
      try {
        localStorage.setItem("pro_fb_contact", JSON.stringify({
          phone: form.phone, website: form.website, address: form.address,
          destination_url: form.destination_url, offer_text: form.offer_text,
        }));
      } catch (err) { console.debug(err); }
    }
    try {
      const imgs = (form.media || []).filter((m)=>m.type==="image").map((m)=>m.url);
      const payload = {
        ...form,
        media: form.media || [],
        image_url: imgs[0] || "",
        image_secondary_url: imgs[1] || "",
        submit: !asDraft,
      };
      let doc;
      if (editingId) {
        const { data } = await api.put(`/pro/fb-posts/${editingId}`, payload);
        doc = data;
        toast.success(asDraft ? "Brouillon enregistré." : "Aperçu prêt — ouverture dans un nouvel onglet.");
      } else {
        const { data } = await api.post("/pro/fb-posts", payload);
        doc = data;
        toast.success(asDraft ? "Brouillon enregistré." : "Aperçu prêt — ouverture dans un nouvel onglet.");
      }
      if (resetAfter) { setForm(EMPTY); setEditingId(null); }
      if (!asDraft) {
        const url = `${API}/fb-posts/${doc.id}/public`;
        if (previewWin) { try { previewWin.location = url; } catch (err) { previewWin = null; } }
        if (!previewWin) { try { window.open(url, "_blank", "noopener"); } catch (err) { console.debug(err); } }
        setJustPublished(doc);
        try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch (err) { console.debug(err); }
      }
      await loadAll();
    } catch (e) {
      if (previewWin) { try { previewWin.close(); } catch (err) { console.debug(err); } }
      toast.error(e?.response?.data?.detail || "Erreur.");
    } finally { setSaving(false); }
  };

  const runAI = async () => {
    if (!aiBrief.trim() || aiBrief.trim().length < 5) { toast.error("Décris brièvement ta publication."); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post("/pro/fb-posts/ai-draft", {
        brief: aiBrief, tone: aiTone, post_type: form.post_type || "actualite",
        include_contact: aiIncludeContact,
      });
      const hashtagsLine = (data.hashtags || []).map((h) => `#${h}`).join(" ");
      setForm((s) => ({
        ...s,
        title: data.title || s.title,
        text: (data.text || s.text) + (hashtagsLine ? `\n\n${hashtagsLine}` : ""),
      }));
      setAiBrief("");
      toast.success("Brouillon IA généré. Relis et ajuste avant de publier.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "L'assistant IA n'a pas pu répondre.");
    } finally { setAiLoading(false); }
  };

  const runAiImage = async () => {
    const prompt = (aiBrief.trim() || form.title.trim() || form.text.trim());
    if (prompt.length < 3) { toast.error("Décris l'image souhaitée dans le brief (ou saisis un titre)."); return; }
    setAiImgLoading(true);
    try {
      const { data } = await api.post("/pro/fb-posts/ai-image", { prompt });
      if (data?.url) {
        setForm((s) => ({ ...s, media: [...(s.media || []), { type: "image", url: data.url, ai: true, prompt }].slice(0, MAX_MEDIA) }));
        toast.success("Image IA générée et ajoutée. Vous pouvez la régénérer ou la recadrer.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "La génération d'image a échoué.");
    } finally { setAiImgLoading(false); }
  };

  const regenerateImage = async (i) => {
    const item = (form.media || [])[i];
    const prompt = (item?.prompt || form.title.trim() || form.text.trim());
    if (!prompt || prompt.length < 3) { toast.error("Impossible de régénérer : brief manquant."); return; }
    setRegenIdx(i);
    try {
      const { data } = await api.post("/pro/fb-posts/ai-image", { prompt });
      if (data?.url) {
        setForm((s) => ({ ...s, media: (s.media || []).map((m, k) => (k === i ? { ...m, url: data.url, ai: true, prompt } : m)) }));
        toast.success("Nouvelle image générée.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "La régénération a échoué.");
    } finally { setRegenIdx(-1); }
  };

  const isMember = entity.kind === "member";
  const typeLabel = useMemo(() => ({ pro: "commerce", club: "club", association: "association", organization: "organisme", member: "votre profil" }[entity.kind] || "page"), [entity]);

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-2" data-testid="pro-fb-title">
            <Facebook className="text-blue-600" size={26}/> Créer une publication Facebook &amp; groupes
          </h1>
          <p className="text-slate-500 text-xs md:text-sm mt-1">{isMember
            ? <>Composez votre message et partagez-le directement sur votre profil Facebook ou dans vos groupes — <b>publication illimitée</b>, sans validation.</>
            : <>Composez votre publication et diffusez-la directement sur votre profil, votre page ou dans les groupes Facebook — <b>publication illimitée</b>, sans validation.</>}</p>
        </div>
        <Button variant="outline" onClick={loadAll} disabled={loading} data-testid="pro-fb-reload">
          <RefreshCw size={14} className={`mr-1 ${loading ? "animate-spin" : ""}`}/> Actualiser
        </Button>
      </div>

      {justPublished && (
        <div className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4" data-testid="pro-fb-justpublished">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2 text-emerald-800 font-heading font-extrabold">
              <CheckCircle2 size={18}/> Votre aperçu est prêt — Réf. <span className="font-mono">{justPublished.ref_code}</span>
            </div>
            <Button variant="ghost" size="sm" onClick={()=>setJustPublished(null)} className="text-slate-500" data-testid="pro-fb-close-justpublished"><X size={14} className="mr-1"/> Fermer</Button>
          </div>
          <p className="mt-1 text-sm text-slate-600">La page d'aperçu s'est ouverte dans un nouvel onglet : vous y trouverez tous les boutons de partage (Facebook, groupes, WhatsApp, X, LinkedIn, Email…).</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button type="button" onClick={()=>openPreview(justPublished.id)} className="btn-shine text-white" data-testid="pro-fb-open-preview">
              <Eye size={15} className="mr-1"/> Rouvrir la page d'aperçu & partager
            </Button>
            <Button type="button" variant="outline" onClick={()=>{ copyTextReliable(previewUrl(justPublished.id)); toast.success("Lien copié !"); }} data-testid="pro-fb-copy-preview-link">
              <Copy size={14} className="mr-1"/> Copier le lien
            </Button>
          </div>
        </div>
      )}

      {/* Formulaire + Preview */}
      <div className="mt-6 grid lg:grid-cols-5 gap-5" data-testid="pro-fb-form-wrap">
        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-2xl p-5" data-testid="pro-fb-form">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Sparkles className="text-[#E05638]" size={18}/>
            <h2 className="font-heading text-lg font-bold" data-testid="pro-fb-form-heading">{editingId ? "Modifier la publication" : "Nouvelle publication"}</h2>
          </div>
          {editingId && (
            <Button type="button" variant="ghost" size="sm" onClick={cancelEdit} className="text-slate-500" data-testid="pro-fb-cancel-edit">
              <X size={14} className="mr-1"/> Annuler
            </Button>
          )}
        </div>

        <div className="mt-4 grid md:grid-cols-3 gap-3">
          <div>
            <Label>Type</Label>
            <Select value={form.post_type} onValueChange={(v)=>set("post_type", v)}>
              <SelectTrigger data-testid="pro-fb-post-type"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="actualite">Actualité</SelectItem>
                <SelectItem value="offre">Offre / promo</SelectItem>
                <SelectItem value="evenement">Événement</SelectItem>
                <SelectItem value="nouveau_produit">Nouveauté</SelectItem>
                <SelectItem value="autre">Autre</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col">
            <Label>Date souhaitée</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button type="button" variant="outline" className="mt-2 w-full justify-start font-normal" data-testid="pro-fb-desired-date">
                  <CalendarDays size={14} className="mr-2 text-slate-500"/>
                  {form.desired_date
                    ? format(parse(form.desired_date, "yyyy-MM-dd", new Date()), "d MMM yyyy", { locale: fr })
                    : <span className="text-slate-500">Choisir une date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" locale={fr} initialFocus
                  selected={form.desired_date ? parse(form.desired_date, "yyyy-MM-dd", new Date()) : undefined}
                  onSelect={(d)=>set("desired_date", d ? format(d, "yyyy-MM-dd") : "")}/>
              </PopoverContent>
            </Popover>
          </div>
          <div className="flex items-end">
            <Button type="button" variant="outline" className="w-full" onClick={()=>{ setAiOpen((v)=>!v); setTplOpen(false); }} data-testid="pro-fb-ai-toggle">
              <Wand2 size={14} className="mr-1 text-purple-600"/> Assistant IA
            </Button>
          </div>
        </div>

        <div className="mt-3">
          <Button type="button" variant="outline" onClick={()=>{ setTplOpen((v)=>!v); setAiOpen(false); }} data-testid="pro-fb-tpl-toggle">
            <LayoutTemplate size={14} className="mr-1 text-[#E05638]"/> Modèles prêts à l'emploi
          </Button>
          {tplOpen && (
            <div className="mt-2 bg-orange-50 border border-orange-200 rounded-xl p-3" data-testid="pro-fb-tpl-block">
              <div className="text-xs text-orange-900 font-semibold mb-2">Choisissez un modèle par secteur — il remplit le titre et le texte, à personnaliser ensuite.</div>
              <div className="grid sm:grid-cols-2 gap-2 max-h-72 overflow-auto pr-1">
                {orderTemplates(entity).map((t, i) => (
                  <button key={i} type="button" onClick={()=>applyTemplate(t)}
                          className="text-left bg-white border border-slate-200 rounded-lg p-2.5 hover:border-[#E05638] hover:bg-orange-50/50 transition-colors"
                          data-testid={`pro-fb-tpl-${i}`}>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-[#E05638]">{t.sector}</div>
                    <div className="text-sm font-semibold text-slate-900 line-clamp-1">{t.title}</div>
                    <div className="text-[11px] text-slate-500 line-clamp-2 whitespace-pre-line mt-0.5">{t.text}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {aiOpen && (
          <div className="mt-3 bg-purple-50 border border-purple-200 rounded-xl p-3" data-testid="pro-fb-ai-block">
            <Label className="text-purple-900">Brief pour l'assistant IA</Label>
            <Textarea rows={2} value={aiBrief} onChange={(e)=>setAiBrief(e.target.value)}
                      placeholder={isMember
                        ? "Ex : je donne un vélo enfant en bon état à récupérer à Chartres ce week-end."
                        : "Ex : Offre du weekend -20% sur les menus midi jusqu'à dimanche, mise en avant de notre nouvelle terrasse."}
                      data-testid="pro-fb-ai-brief"/>
            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 items-end">
              <div>
                <Label className="text-purple-900">Ton</Label>
                <Select value={aiTone} onValueChange={setAiTone}>
                  <SelectTrigger data-testid="pro-fb-ai-tone"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="chaleureux">Chaleureux</SelectItem>
                    <SelectItem value="professionnel">Professionnel</SelectItem>
                    <SelectItem value="convivial">Convivial</SelectItem>
                    <SelectItem value="dynamique">Dynamique & fun</SelectItem>
                    <SelectItem value="premium">Premium / élégant</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="button" onClick={runAI} disabled={aiLoading} className="btn-shine text-white" data-testid="pro-fb-ai-run">
                <Wand2 size={14} className="mr-1"/> {aiLoading ? "Génération…" : "Générer le brouillon"}
              </Button>
            </div>
            <label className="mt-2 flex items-center gap-2 text-xs text-purple-900 cursor-pointer" data-testid="pro-fb-ai-contact-label">
              <input type="checkbox" checked={aiIncludeContact} onChange={(e)=>setAiIncludeContact(e.target.checked)} className="accent-[#E05638]" data-testid="pro-fb-ai-include-contact"/>
              Ajouter automatiquement mes coordonnées (site web, téléphone) dans le texte généré
            </label>
            <div className="mt-2 pt-2 border-t border-purple-200">
              <Button type="button" variant="outline" onClick={runAiImage} disabled={aiImgLoading} data-testid="pro-fb-ai-image">
                <ImagePlus size={14} className="mr-1 text-purple-600"/> {aiImgLoading ? "Création de l'image…" : "Générer une image IA"}
              </Button>
              <span className="ml-2 text-[11px] text-purple-800">Créée depuis le brief, ajoutée à vos médias (même endroit que vos photos).</span>
            </div>
            <div className="text-[11px] text-purple-800 mt-2">L'IA propose un brouillon — vous restez libre de le modifier avant de publier.</div>
          </div>
        )}

        <div className="mt-4">
          <Label>Titre *</Label>
          <Input value={form.title} onChange={(e)=>set("title", e.target.value)} placeholder={isMember ? "Ex : Je donne un canapé à Chartres !" : "Ex : Week-end gourmand à Chartres !"} maxLength={200} data-testid="pro-fb-title-input"/>
        </div>
        <div className="mt-3">
          <Label>Texte de la publication * ({form.text.length}/3000)</Label>
          <Textarea rows={6} value={form.text} onChange={(e)=>set("text", e.target.value)}
                    placeholder="Rédigez votre message tel que vous souhaitez qu'il apparaisse sur Facebook."
                    maxLength={3000} data-testid="pro-fb-text-input"/>
        </div>

        <div className="mt-4">
          <MediaUploader media={form.media} onChange={(m)=>set("media", m)} disabled={false} onRegenerate={regenerateImage} regeneratingIdx={regenIdx}/>
        </div>

        {/* Rappel coordonnées */}
        <div className="mt-3 rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-900 flex items-start gap-2" data-testid="pro-fb-contact-reminder">
          <Info size={14} className="mt-0.5 shrink-0 text-emerald-600"/>
          <span>{isMember
            ? <>Ajoutez au besoin vos <b>informations utiles</b> dans le texte (lieu, date, contact…) — c'est ce que vos amis et voisins verront sur Facebook.</>
            : <>N'oubliez pas d'indiquer vos <b>coordonnées</b> dans le texte (site internet, téléphone, e-mail, adresse…) — c'est ce que vos clients verront sur Facebook.</>}</span>
        </div>

        {/* Infos complémentaires — secondaires (grisées) + mémorisation */}
        <div className="mt-3 rounded-xl border border-slate-200 bg-slate-100/70 p-3" data-testid="pro-fb-contact-fields">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <div className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Infos complémentaires (optionnel)</div>
            <label className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-600 cursor-pointer" data-testid="pro-fb-remember-contact">
              <input type="checkbox" checked={rememberContact} onChange={(e)=>setRememberContact(e.target.checked)} className="accent-[#E05638]"/>
              Mémoriser mes coordonnées
            </label>
          </div>
          <div className="grid md:grid-cols-2 gap-3 opacity-80">
            <div><Label className="text-slate-500">Lien de destination</Label>
              <Input value={form.destination_url} onChange={(e)=>set("destination_url", e.target.value)} placeholder="https://…" className="bg-white"/></div>
            <div><Label className="text-slate-500">{isMember ? "Sujet / objet" : "Offre concernée"}</Label>
              <Input value={form.offer_text} onChange={(e)=>set("offer_text", e.target.value)} placeholder={isMember ? "Ex : vide-grenier, objet à donner…" : "Ex : -20% menu midi"} className="bg-white"/></div>
            <div><Label className="text-slate-500">Téléphone</Label>
              <Input value={form.phone} onChange={(e)=>set("phone", e.target.value)} placeholder="02 37 …" className="bg-white"/></div>
            <div><Label className="text-slate-500">Site internet</Label>
              <Input value={form.website} onChange={(e)=>set("website", e.target.value)} placeholder="https://…" className="bg-white"/></div>
            <div className="md:col-span-2"><Label className="text-slate-500">Adresse</Label>
              <Input value={form.address} onChange={(e)=>set("address", e.target.value)} placeholder="10 rue …, Chartres" className="bg-white"/></div>
          </div>
          <p className="mt-2 text-[11px] text-slate-500">Cochez « Mémoriser mes coordonnées » pour les pré-remplir automatiquement à chaque nouveau post.</p>
        </div>

        <div className="mt-4 rounded-xl bg-orange-50 border border-orange-200 px-3 py-2 text-xs text-orange-900 flex items-start gap-2">
          <Shield size={14} className="mt-0.5 shrink-0 text-[#E05638]"/>
          <span>Un lien discret PROXIreduc + un code de référence sont ajoutés automatiquement au texte, et une pastille sur vos images — pour une diffusion traçable.</span>
        </div>

        <FbShareTuto className="mt-4 border-t border-slate-100 pt-3" />

        <div className="mt-4 flex flex-wrap gap-2 justify-end">
          <Button type="button" variant="outline" onClick={()=>submit(false, false)} disabled={saving} data-testid="pro-fb-preview-btn">
            <Eye size={14} className="mr-1"/> {saving ? "…" : "Aperçu"}
          </Button>
          <Button type="button" onClick={()=>submit(false, true)} disabled={saving} className="btn-shine text-white" data-testid="pro-fb-submit">
            <Send size={14} className="mr-1"/> {saving ? "…" : (editingId ? "Mettre à jour & publier" : "Enregistrer et publier")}
          </Button>
        </div>
        </div>

        {/* Live preview */}
        <div className="lg:col-span-2" data-testid="pro-fb-preview-wrap">
          <div className="lg:sticky lg:top-6">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1">
                <Eye size={12}/> Prévisualisation Facebook
              </div>
              <span className="text-[10px] text-slate-400">temps réel</span>
            </div>
            <FbPreview title={form.title} text={form.text}
                       images={(form.media || []).filter((m)=>m.type==="image").map((m)=>m.url)}
                       videos={(form.media || []).filter((m)=>m.type==="video").map((m)=>m.url)}
                       destinationUrl={form.destination_url}
                       businessName={entity.name || (isMember ? "Votre profil" : "Votre page")} businessCity={entity.city}/>
            <p className="mt-3 text-[11px] text-slate-500 leading-relaxed">
              Cette prévisualisation reproduit le rendu d'un post Facebook pour {typeLabel}. La mise en page réelle peut varier légèrement selon l'appareil.
            </p>
          </div>
        </div>
      </div>

      {/* Historique */}
      <div className="mt-8">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h2 className="font-heading text-xl font-bold flex items-center gap-2">Historique de mes publications</h2>
          {posts.length > 0 && (
            <div className="flex items-center gap-2">
              <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 cursor-pointer select-none" data-testid="pro-fb-select-all">
                <input type="checkbox" checked={posts.length > 0 && selected.size === posts.length} onChange={toggleAll} className="accent-[#E05638] w-4 h-4"/>
                Tout sélectionner
              </label>
              {selected.size > 0 && (
                <Button size="sm" variant="outline" className="border-rose-300 text-rose-600 hover:bg-rose-50" onClick={()=>{ setPendingIds(Array.from(selected)); setConfirmOpen(true); }} data-testid="pro-fb-bulk-delete">
                  <Trash2 size={13} className="mr-1"/> Supprimer ({selected.size})
                </Button>
              )}
            </div>
          )}
        </div>
        {loading ? (
          <div className="text-slate-500 text-sm mt-3">Chargement…</div>
        ) : posts.length === 0 ? (
          <div className="text-slate-500 text-sm mt-3 bg-white border border-dashed rounded-2xl p-8 text-center">Aucune publication pour l'instant.</div>
        ) : (
          <div className="mt-3 space-y-3">
            {posts.map((p) => (
              <div key={p.id} className={`bg-white border rounded-2xl p-4 transition-colors ${selected.has(p.id) ? "border-[#E05638] ring-1 ring-[#E05638]" : "border-slate-200"}`} data-testid={`pro-fb-item-${p.id}`}>
                <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-start sm:justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <input type="checkbox" checked={selected.has(p.id)} onChange={()=>toggleOne(p.id)} className="mt-1 accent-[#E05638] w-4 h-4 shrink-0 cursor-pointer" data-testid={`pro-fb-select-${p.id}`} aria-label="Sélectionner cette publication"/>
                    <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {p.status === "brouillon"
                        ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-slate-100 text-slate-700"><Save size={11}/> Brouillon</span>
                        : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-emerald-600 text-white"><CheckCircle2 size={11}/> Prête à diffuser</span>}
                      {p.ref_code && <span className="text-[11px] font-mono font-bold px-1.5 py-0.5 rounded bg-orange-50 text-[#E05638] border border-orange-200" data-testid={`pro-fb-ref-${p.id}`}>{p.ref_code}</span>}
                      {p.clicks > 0 && <span className="inline-flex items-center gap-1 text-[11px] font-bold text-slate-600" data-testid={`pro-fb-clicks-${p.id}`}><Eye size={11}/> {p.clicks} visite{p.clicks>1?"s":""}</span>}
                      <span className="text-[11px] text-slate-500">{new Date(p.created_at).toLocaleString("fr-FR")}</span>
                    </div>
                    <div className="mt-1 font-heading font-bold text-slate-900">{p.title}</div>
                    <div className="text-sm text-slate-600 line-clamp-3 whitespace-pre-line">{p.text}</div>
                    {(() => {
                      const hm = (p.media && p.media.length) ? p.media : (p.image_url ? [{ type: "image", url: p.image_url }] : []);
                      if (!hm.length) return null;
                      return (
                        <div className="mt-2.5 flex flex-wrap gap-1.5" data-testid={`pro-fb-item-media-${p.id}`}>
                          {hm.slice(0, 4).map((m, i) => (
                            <div key={m.url || i} className="relative w-16 h-16 rounded-lg overflow-hidden border border-slate-200 bg-slate-900">
                              {m.type === "video"
                                ? <><video src={m.url} className="w-full h-full object-cover" muted/><div className="absolute inset-0 grid place-items-center text-white/90"><Play size={16} fill="currentColor"/></div></>
                                : <img src={m.url} alt="" className="w-full h-full object-cover"/>}
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                  </div>
                  <div className="flex flex-row sm:flex-col items-center sm:items-end gap-1.5 flex-wrap pl-7 sm:pl-0">
                    <Button type="button" size="sm" variant="outline" onClick={()=>startEdit(p)}
                            className="border-[#E05638] text-[#E05638] hover:bg-orange-50" data-testid={`pro-fb-edit-${p.id}`}>
                      <Pencil size={13} className="mr-1"/> Modifier
                    </Button>
                    <Button type="button" size="sm" onClick={()=>openPreview(p.id)}
                            className="btn-shine text-white" data-testid={`pro-fb-share-${p.id}`}>
                      <Eye size={13} className="mr-1"/> Aperçu &amp; partager
                    </Button>
                    <Button type="button" size="sm" variant="ghost" onClick={()=>{ setPendingIds([p.id]); setConfirmOpen(true); }}
                            className="text-rose-600 hover:bg-rose-50" data-testid={`pro-fb-delete-${p.id}`}>
                      <Trash2 size={13} className="mr-1"/> Supprimer
                    </Button>
                    {p.fb_post_url && (
                      <a href={p.fb_post_url} target="_blank" rel="noreferrer" className="text-blue-700 text-sm font-semibold inline-flex items-center gap-1 hover:underline">
                        Voir sur Facebook <ExternalLink size={12}/>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="pro-fb-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer {pendingIds.length} publication{pendingIds.length > 1 ? "s" : ""} ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est définitive. Les publications sélectionnées seront retirées de votre historique.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="pro-fb-delete-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={(e)=>{ e.preventDefault(); doBulkDelete(); }} disabled={deleting}
                               className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="pro-fb-delete-confirm-btn">
              {deleting ? "Suppression…" : "Supprimer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
